// =============================================================================
// ModelImagesManager — staff-only image management for a product model.
// =============================================================================
// Upload photos straight to object storage (DigitalOcean Spaces in prod),
// promote one to primary (the catalogue card / gallery cover), or remove them.
// Shown inline on the model detail page for users with `products.edit`.

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiPatch, apiPost, getApiError } from '../../lib/api';
import { uploadModelImage } from '../../lib/upload';

interface ModelImage {
  id: string;
  url: string;
  isPrimary: boolean;
}

export function ModelImagesManager({
  modelId,
  images,
}: {
  modelId: string;
  images: ModelImage[];
}): JSX.Element {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['product-model', modelId] });
    void qc.invalidateQueries({ queryKey: ['product-models'] });
  };

  const register = useMutation({
    mutationFn: (publicUrl: string) =>
      apiPost(`/product-models/${modelId}/images`, {
        url: publicUrl,
        isPrimary: images.length === 0,
      }),
    onSuccess: invalidate,
  });
  const setPrimary = useMutation({
    mutationFn: (imageId: string) =>
      apiPatch(`/product-models/${modelId}/images/${imageId}`, { isPrimary: true }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (imageId: string) => apiDelete(`/product-models/${modelId}/images/${imageId}`),
    onSuccess: invalidate,
  });

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      // Sequential keeps the "first image becomes primary" rule predictable.
      for (const file of files) {
        const url = await uploadModelImage(modelId, file);
        await register.mutateAsync(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : getApiError(err).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="rounded-md border border-rule bg-paper-2/40 p-3 space-y-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-3 font-medium">
        Manage images
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded overflow-hidden border border-rule">
              <img src={img.url} alt="" className="w-full aspect-square object-cover" />
              {img.isPrimary ? (
                <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-accent text-white rounded">
                  Primary
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setPrimary.mutate(img.id)}
                  disabled={setPrimary.isPending}
                  className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-ink/60 text-white rounded opacity-0 group-hover:opacity-100 transition hover:bg-accent"
                >
                  Set primary
                </button>
              )}
              <button
                type="button"
                onClick={() => remove.mutate(img.id)}
                disabled={remove.isPending}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-ink/60 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-xs leading-none hover:bg-negative"
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/avif,image/gif,image/svg+xml"
          onChange={onPick}
          disabled={uploading}
          className="block text-sm text-ink-2 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-accent file:text-white file:cursor-pointer hover:file:bg-accent-2 file:transition"
        />
        {uploading && <div className="text-xs text-ink-3 mt-2">Uploading…</div>}
        {error && <div className="text-xs text-negative mt-2">{error}</div>}
        {!uploading && !error && images.length === 0 && (
          <div className="text-xs text-ink-3 mt-2 italic">
            No images yet — the first one you add becomes the primary.
          </div>
        )}
      </div>
    </div>
  );
}
